/**
 * Webhook Received Event Component
 *
 * Renders ahoy_webhook_received events on the timeline.
 * Header: "**Actor** received a webhook"
 * Body: payload rendered as a JSON code block via MarkdownContent.
 */

import { useCallback, useId, useMemo, useState } from 'react';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';

import {
  ActorName,
  bold,
  InlineText,
  TimelineEvent,
} from '../components/shared';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import { TIMELINE_DESIGN_TOKENS } from '../design-tokens';
import { useEventFormatters } from '../useEventFormatters';
import type { EventComponentProps } from './types';

export function WebhookReceivedEvent({ event }: EventComponentProps) {
  const { getActorInfo } = useEventFormatters();
  const { lookups } = useViewStateLookups();
  const actor = getActorInfo(event);
  const payloadId = useId();
  const { payload } = event;

  const webhookName = useMemo(() => {
    const displayName = lookups.entities[event.entity_id]?.ahoy_display_name;
    if (!displayName) {
      return undefined;
    }
    // Only trim a standalone "webhook" suffix (preceded by whitespace or at
    // the start of the string) so names like "MyWebhook" are left intact.
    const trimmed = displayName.replace(/(?:^|\s)webhook\s*$/i, '').trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }, [lookups.entities, event.entity_id]);

  const ariaLabel = webhookName
    ? `${actor.name} received a webhook – ${webhookName}`
    : `${actor.name} received a webhook`;

  const payloadMarkdown = useMemo(() => {
    if (!payload || Object.keys(payload).length === 0) {
      return undefined;
    }
    return `\`\`\`json\n${JSON.stringify(payload, null, 2)}\n\`\`\``;
  }, [payload]);

  const [showPayload, setShowPayload] = useState(false);
  const togglePayload = useCallback(() => setShowPayload(prev => !prev), []);

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <InlineText>
            <ActorName actor={actor} /> received a webhook
            {webhookName ? <> — {bold(webhookName)}</> : null}
          </InlineText>
        }
        timestamp={event.occurred_at}
      >
        {payloadMarkdown && (
          <div id={payloadId} className='mt-2'>
            <button
              type='button'
              onClick={togglePayload}
              aria-expanded={showPayload}
              aria-controls={payloadId}
              className={`${TIMELINE_DESIGN_TOKENS.typography.subItem} text-fg-tertiary hover:text-fg-secondary cursor-pointer bg-transparent border-none p-0`}
            >
              {showPayload ? 'Hide payload' : 'Show payload'}
            </button>
            {showPayload && (
              <MarkdownContent
                content={payloadMarkdown}
                size='compact'
                className='mt-2'
              />
            )}
          </div>
        )}
      </TimelineEvent.Content>
    </TimelineEvent>
  );
}
